import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout';
import { CheckCircle, XCircle, Clock, Search, Filter, X, Plus, BookOpen, BarChart3, Eye, UserX, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../api/examApi';

const CLASS_LEVELS = [6, 7, 8, 9, 10, 11, 12];

const STATUS_LABELS = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  rejected: { label: 'Rejected', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_LABELS[status] || { label: status, color: 'bg-slate-100 text-slate-600' };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${cfg.color}`}>
      {status === 'pending' && <Clock size={11} />}
      {status === 'approved' && <CheckCircle size={11} />}
      {status === 'rejected' && <XCircle size={11} />}
      {cfg.label}
    </span>
  );
}

const FEE_DOT_TITLE = { overdue: 'Fees overdue', soft: 'Fees due soon' };
const FEE_DOT_COLOR = { overdue: 'bg-red-500', soft: 'bg-amber-500' };

function FeeDot({ level }) {
  if (!level) return null;
  return (
    <span
      className={`inline-block h-2.5 w-2.5 rounded-full ${FEE_DOT_COLOR[level]}`}
      title={FEE_DOT_TITLE[level]}
      data-testid={`fee-dot-${level}`}
    />
  );
}

export default function AdminStudentsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [studentData, setStudentData] = useState({ data: [], total: 0 });
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [activeFilter, setActiveFilter] = useState('');
  const [feesFilter, setFeesFilter] = useState(searchParams.get('fees') || '');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null);

  const loadStudents = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: 10 };
    if (search) params.search = search;
    if (statusFilter) params.registration_status = statusFilter;
    if (classFilter) params.class_level = classFilter;
    if (activeFilter) params.is_active = activeFilter === 'active';
    if (feesFilter) params.fees = feesFilter;
    adminApi.listStudents(params)
      .then(({ data }) => setStudentData(data))
      .catch(() => toast.error('Failed to load students'))
      .finally(() => setLoading(false));
  }, [page, search, statusFilter, classFilter, activeFilter, feesFilter]);

  useEffect(() => { loadStudents(); }, [loadStudents]);

  const ACTION_FNS = {
    approve: adminApi.approveStudent,
    reject: adminApi.rejectStudent,
    deactivate: adminApi.deactivateStudent,
    reactivate: adminApi.reactivateStudent,
  };

  const act = async (studentId, action) => {
    setActioning(studentId);
    try {
      const { data: updated } = await ACTION_FNS[action](studentId);
      setStudentData((prev) => ({
        ...prev,
        data: prev.data.map((s) => (s.id === studentId ? updated : s)),
      }));
      toast.success(`Student ${action}d`);
    } catch {
      toast.error(`Failed to ${action} student`);
    } finally {
      setActioning(null);
    }
  };

  const handleDeactivate = (student) => {
    if (!window.confirm(`Deactivate ${student.name}? They will not be able to log in, and will disappear from group and exam-assignment pickers. Their history stays intact and reactivation is available any time.`)) {
      return;
    }
    act(student.id, 'deactivate');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    loadStudents();
  };

  const clearFilters = () => {
    setSearch('');
    setClassFilter('');
    setStatusFilter('');
    setActiveFilter('');
    setFeesFilter('');
    setPage(1);
  };

  const totalPages = Math.ceil(studentData.total / 10) || 1;

  return (
    <DashboardLayout title="Student Directory">
      <div className="animate-fade-in space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
              Student Directory
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Manage all students — registrations, profiles, and exam ledgers
            </p>
          </div>
          <Link
            to="/admin/students/new"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary-light text-white shadow-sm rounded-md px-4 py-2.5 transition-colors duration-200 font-medium"
            data-testid="create-student-btn"
          >
            <Plus size={20} />
            Create Student
          </Link>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, or phone..."
                className="w-full pl-10 pr-4 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <select
                value={classFilter}
                onChange={(e) => {
                  setClassFilter(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors"
                data-testid="class-filter"
              >
                <option value="">All Classes</option>
                {CLASS_LEVELS.map((c) => (
                  <option key={c} value={c}>Class {c}</option>
                ))}
              </select>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  className="pl-9 pr-8 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors appearance-none"
                  data-testid="status-filter"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <select
                value={activeFilter}
                onChange={(e) => {
                  setActiveFilter(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors"
                data-testid="active-filter"
              >
                <option value="">Active + Inactive</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
              <select
                value={feesFilter}
                onChange={(e) => {
                  setFeesFilter(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors"
                data-testid="fees-filter"
              >
                <option value="">All Fees</option>
                <option value="overdue">Overdue</option>
                <option value="due_soon">Due Soon</option>
                <option value="attention">Overdue or Due Soon</option>
              </select>
              {(search || statusFilter || classFilter || activeFilter || feesFilter) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                  data-testid="clear-filters-btn"
                  title="Clear filters"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm">
          {loading ? (
            <div className="animate-pulse">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center p-4 border-b border-slate-100 dark:border-slate-800">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mr-4"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mr-4"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mr-4"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/6"></div>
                </div>
              ))}
            </div>
          ) : studentData.data.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm">No students found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Name</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Class</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Board</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Subjects</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Student Contact</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Status</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Registered</th>
                  <th className="px-5 py-3 text-right font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {studentData.data.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors" data-testid={`student-row-${s.id}`}>
                    <td className="px-5 py-3">
                      <p className="font-medium text-slate-900 dark:text-white flex items-center gap-2">
                        <FeeDot level={s.fee_level} />
                        {s.name}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{s.email}</p>
                    </td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">{s.class_level ?? '—'}</td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">{s.board ?? '—'}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(s.subjects || []).slice(0, 2).map((subject, idx) => (
                          <span
                            key={idx}
                            className="bg-primary/10 text-primary dark:bg-accent-cool/20 dark:text-accent-cool px-2 py-0.5 rounded text-xs"
                          >
                            {subject}
                          </span>
                        ))}
                        {(s.subjects || []).length > 2 && (
                          <span className="text-xs text-slate-500">+{s.subjects.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">{s.phone_number ?? '—'}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        <StatusBadge status={s.registration_status} />
                        {!s.is_active && (
                          <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                            <UserX size={11} /> Inactive
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <button
                          onClick={() => navigate(`/admin/students/${s.id}`)}
                          className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-600 dark:text-slate-400"
                          data-testid={`view-student-${s.id}`}
                          title="View/Edit profile"
                        >
                          <Eye size={18} />
                        </button>
                        {(s.registration_status === 'pending' || s.registration_status === 'rejected') && (
                          <button
                            disabled={actioning === s.id}
                            onClick={() => act(s.id, 'approve')}
                            className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold text-white transition-colors"
                          >
                            <CheckCircle size={13} /> Approve
                          </button>
                        )}
                        {s.registration_status === 'pending' && (
                          <button
                            disabled={actioning === s.id}
                            onClick={() => act(s.id, 'reject')}
                            className="inline-flex items-center gap-1 rounded-lg bg-rose-500 hover:bg-rose-600 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold text-white transition-colors"
                          >
                            <XCircle size={13} /> Reject
                          </button>
                        )}
                        {s.registration_status === 'approved' && (
                          <>
                            <button
                              onClick={() => navigate(`/admin/students/${s.id}/ledger`)}
                              className="inline-flex items-center gap-1 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 transition-colors"
                            >
                              <BookOpen size={13} /> Ledger
                            </button>
                            <button
                              onClick={() => navigate(`/admin/reports/students/${s.id}`)}
                              className="inline-flex items-center gap-1 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 transition-colors"
                              data-testid={`report-student-${s.id}`}
                            >
                              <BarChart3 size={13} /> Report
                            </button>
                            {s.is_active ? (
                              <button
                                disabled={actioning === s.id}
                                onClick={() => handleDeactivate(s)}
                                className="inline-flex items-center gap-1 rounded-lg bg-slate-500 hover:bg-slate-600 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold text-white transition-colors"
                                data-testid={`deactivate-student-${s.id}`}
                              >
                                <UserX size={13} /> Deactivate
                              </button>
                            ) : (
                              <button
                                disabled={actioning === s.id}
                                onClick={() => act(s.id, 'reactivate')}
                                className="inline-flex items-center gap-1 rounded-lg bg-primary hover:bg-primary-light disabled:opacity-50 px-3 py-1.5 text-xs font-semibold text-white transition-colors"
                                data-testid={`reactivate-student-${s.id}`}
                              >
                                <UserCheck size={13} /> Reactivate
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Showing {((page - 1) * 10) + 1} to {Math.min(page * 10, studentData.total)} of {studentData.total} results
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="prev-page-btn"
              >
                Previous
              </button>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="next-page-btn"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
